import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, UserRound } from "lucide-react";

import { PRESENTING_PROBLEMS } from "../../../../shared/domain/presentingProblems";
import { THERAPY_TYPES } from "../../../../shared/domain/therapyTypes";
import TagFinder, { type TagFinderOption } from "../../../../shared/ui/form/TagFinder";
import {
    CONTENT_LANGUAGES,
    languageLabelKey,
} from "../../../../shared/i18n/contentLanguages";
import { ActiveFilterChips, FilterChip, FiltersToggle } from "../../../../shared/ui/form/FilterControls";
import {
    MeditationFilters,
    MeditationKind,
    PresentingProblem,
    TherapyType,
} from "../../domain/types";

type Props = {
    filters: MeditationFilters;
    onChange: (next: MeditationFilters) => void;
    /** Labels the loaded meditations carry that the catalogue does not. */
    ownTherapyTypes?: string[];
    ownProblems?: string[];
    /** Whether to look by author: in a catalogue written by more than one person. */
    showAuthor?: boolean;
    /** Whether to offer ranking by likes: only the public catalogue has them. */
    showOrder?: boolean;
};



/**
 * Narrowing a library.
 *
 * Four fields: what it is called, the way of working it belongs to, what it is
 * for, and what language it is in. The same four narrow the therapist's own
 * meditations and the public ones — they are looking for the same thing in
 * both places, and two vocabularies for one search would only mean learning
 * it twice.
 *
 * Language is a plain select rather than the search-and-pick control: there
 * are seven, and a therapist knows which one they want before they look.
 *
 * The two tag fields are the search-and-pick control the scheduler and
 * payments use for a patient, because the problem is the same: a catalogue too
 * long to be a menu. A native dropdown of twenty-nine therapies covered the
 * library it was meant to be filtering.
 *
 * They take what the therapist types, too. A meditation can be filed under a
 * word the catalogue has no entry for, and a library you can tag something in
 * but not find it by is not a library.
 *
 * What it is made of narrows too: a therapist who wants a voice to play in
 * the room, rather than a script to read aloud themselves, should not have to
 * open every card to find one. Two choices rather than the three kinds, since
 * "has a recording" is the question; whether it also has the script is not.
 *
 * And in a catalogue others write, who wrote it: once a therapist has found an
 * author whose meditations work for them, the next one they want is that
 * author's.
 *
 * Only the search is always in view, with the order beside it where there is
 * one: six fields open at once pushed the meditations below the fold on a
 * phone, and most visits only ever type a word. The rest open under
 * "Filtros", and whatever is set shows as a chip that takes it off again —
 * so a narrowed library always says why it is narrow.
 */
export default function MeditationFilterBar({
    filters,
    onChange,
    ownTherapyTypes = [],
    ownProblems = [],
    showAuthor = false,
    showOrder = false,
}: Props) {
    const { t } = useTranslation();

    const therapyOptions: TagFinderOption[] = [
        ...THERAPY_TYPES.map((value) => ({
            value,
            label: t(`session.therapyOptions.${value}`),
        })),
        ...ownTherapyTypes.map((label) => ({ value: label, label })),
    ];
    const problemOptions: TagFinderOption[] = [
        ...PRESENTING_PROBLEMS.map((value) => ({
            value,
            label: t(`publicDirectory.problems.${value}`),
        })),
        ...ownProblems.map((label) => ({ value: label, label })),
    ];

    // Everything but the search and the order: what the chips show.
    const chips: FilterChip[] = [];
    if (filters.therapyType)
        chips.push({
            key: "therapyType",
            label:
                therapyOptions.find((o) => o.value === filters.therapyType)?.label ??
                filters.therapyType,
        });
    if (filters.presentingProblem)
        chips.push({
            key: "presentingProblem",
            label:
                problemOptions.find((o) => o.value === filters.presentingProblem)?.label ??
                filters.presentingProblem,
        });
    if (filters.language)
        chips.push({ key: "language", label: t(languageLabelKey(filters.language)) });
    if (filters.kind)
        chips.push({
            key: "kind",
            label: t(
                filters.kind === MeditationKind.TEXT
                    ? "meditation.filters.kindText"
                    : "meditation.filters.kindVoice"
            ),
        });
    if (showAuthor && filters.author)
        chips.push({
            key: "author",
            label: t("meditation.byAuthor", { author: filters.author }),
        });

    const [expanded, setExpanded] = useState(chips.length > 0);

    const clear = (key: string) => onChange({ ...filters, [key]: undefined });
    // The chips go; what was typed and the order stay, as they are in view.
    const clearAll = () => onChange({ search: filters.search, order: filters.order });

    return (
        <div className="mb-5 flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
                <label className="flex min-w-0 flex-1 basis-60 flex-col gap-1 text-xs font-medium text-content-tertiary">
                    {t("meditation.filters.search")}
                    <span className="relative">
                        <Search
                            size={15}
                            aria-hidden="true"
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-tertiary"
                        />
                        <input
                            type="search"
                            className="field-base field-default h-11 w-full py-2 pl-9 pr-3 text-sm"
                            placeholder={t("meditation.filters.searchPlaceholder")}
                            value={filters.search ?? ""}
                            onChange={(e) =>
                                onChange({ ...filters, search: e.target.value || undefined })
                            }
                        />
                    </span>
                </label>

                {showOrder && (
                    <label className="flex w-full flex-col gap-1 text-xs font-medium text-content-tertiary sm:w-48">
                        {t("meditation.filters.order")}
                        <select
                            className="field-base field-default h-11 w-full appearance-none px-4 text-sm"
                            value={filters.order ?? "recent"}
                            onChange={(e) =>
                                onChange({
                                    ...filters,
                                    // Newest first is the default, so it is not sent.
                                    order: e.target.value === "likes" ? "likes" : undefined,
                                })
                            }
                        >
                            <option value="recent">{t("meditation.filters.orderRecent")}</option>
                            <option value="likes">{t("meditation.filters.orderLikes")}</option>
                        </select>
                    </label>
                )}

                <FiltersToggle
                    expanded={expanded}
                    count={chips.length}
                    onToggle={() => setExpanded((value) => !value)}
                />
            </div>

            {expanded && (
                <div className="grid gap-3 rounded-xl border border-border-subtle bg-surface-raised p-3 sm:grid-cols-2 lg:grid-cols-3">
                    <TagFinder
                        label={t("meditation.filters.therapyType")}
                        options={therapyOptions}
                        value={filters.therapyType}
                        allowCustom
                        placeholder={t("meditation.filters.anyOrType")}
                        onChange={(next) =>
                            onChange({ ...filters, therapyType: next as TherapyType | undefined })
                        }
                    />

                    <TagFinder
                        label={t("meditation.filters.presentingProblem")}
                        options={problemOptions}
                        value={filters.presentingProblem}
                        allowCustom
                        placeholder={t("meditation.filters.anyOrType")}
                        onChange={(next) =>
                            onChange({
                                ...filters,
                                presentingProblem: next as PresentingProblem | undefined,
                            })
                        }
                    />

                    <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                        {t("meditation.filters.language")}
                        <select
                            className="field-base field-default h-11 w-full appearance-none px-4 text-sm"
                            value={filters.language ?? ""}
                            onChange={(e) =>
                                onChange({ ...filters, language: e.target.value || undefined })
                            }
                        >
                            <option value="">{t("meditation.filters.anyLanguage")}</option>
                            {CONTENT_LANGUAGES.map((code) => (
                                <option key={code} value={code}>
                                    {t(languageLabelKey(code))}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                        {t("meditation.filters.kind")}
                        <select
                            className="field-base field-default h-11 w-full appearance-none px-4 text-sm"
                            value={filters.kind ?? ""}
                            onChange={(e) =>
                                onChange({
                                    ...filters,
                                    kind: (e.target.value || undefined) as
                                        | MeditationKind
                                        | undefined,
                                })
                            }
                        >
                            <option value="">{t("meditation.filters.anyKind")}</option>
                            <option value={MeditationKind.TEXT}>
                                {t("meditation.filters.kindText")}
                            </option>
                            <option value={MeditationKind.AUDIO}>
                                {t("meditation.filters.kindVoice")}
                            </option>
                        </select>
                    </label>

                    {showAuthor && (
                        <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                            {t("meditation.filters.author")}
                            <span className="relative">
                                <UserRound
                                    size={15}
                                    aria-hidden="true"
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-tertiary"
                                />
                                <input
                                    type="search"
                                    className="field-base field-default h-11 w-full py-2 pl-9 pr-3 text-sm"
                                    placeholder={t("meditation.filters.authorPlaceholder")}
                                    value={filters.author ?? ""}
                                    onChange={(e) =>
                                        onChange({
                                            ...filters,
                                            author: e.target.value || undefined,
                                        })
                                    }
                                />
                            </span>
                        </label>
                    )}
                </div>
            )}

            <ActiveFilterChips chips={chips} onRemove={clear} onClear={clearAll} />
        </div>
    );
}
